import { useEffect, useMemo, useState } from 'react';

import { Autocomplete, Stack, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { ContentClient } from '@@api';
import { useApiClient } from '@@common';
import { StringTemplateViewModel } from '../../../../api/api';

type ContentTemplateInputProps = {
	templateId: string | undefined;
	onTemplateIdChange: (templateId: string | undefined) => void;
	isEditable?: boolean;
};

const categories = ['Email', 'SMS'];

function getTemplateStableId(template: StringTemplateViewModel): string {
	return template.templateId || template.id;
}

export default function ContentTemplateInput({ templateId, onTemplateIdChange, isEditable = true }: ContentTemplateInputProps) {
	const client = useApiClient(ContentClient);
	const [category, setCategory] = useState('');
	const [searchText, setSearchText] = useState('');

	const { data, isFetching } = useQuery({
		queryKey: ['content-template-input', 'templates', category, searchText],
		queryFn: () => client.search(0, 100, 'Name', true, undefined, category || undefined, searchText || undefined, undefined),
	});

	const { data: selectedTemplateData } = useQuery({
		queryKey: ['content-template-input', 'selected-template', templateId],
		queryFn: async () => {
			if (!templateId) {
				return undefined;
			}

			try {
				return await client.getById(templateId);
			} catch {
				return undefined;
			}
		},
		enabled: !!templateId,
	});

	const templates = useMemo(() => {
		const items = data?.items ?? [];
		if (!selectedTemplateData) {
			return items;
		}

		const selectedId = getTemplateStableId(selectedTemplateData);
		if (items.some((template) => getTemplateStableId(template) === selectedId)) {
			return items;
		}

		return [selectedTemplateData, ...items];
	}, [data, selectedTemplateData]);

	const selectedTemplate = useMemo(
		() => templates.find((template) => getTemplateStableId(template) === templateId) ?? null,
		[templates, templateId],
	);

	useEffect(() => {
		if (selectedTemplate && !searchText) {
			setSearchText(selectedTemplate.name ?? '');
		}
	}, [searchText, selectedTemplate]);

	return (
		<Stack
			spacing={1}
			onMouseDown={(event) => event.stopPropagation()}
			onClick={(event) => event.stopPropagation()}
			sx={{ minWidth: 360 }}
		>
			<Stack direction="row" spacing={1}>
				<Autocomplete
					size="small"
					options={categories}
					value={category || null}
					onChange={(_, value) => {
						setCategory(value ?? '');
						onTemplateIdChange(undefined);
					}}
					disabled={!isEditable}
					sx={{ minWidth: 140 }}
					disablePortal
					clearText="Clear category"
					renderInput={(params) => <TextField {...params} label="Category" />}
				/>
				<Autocomplete
					size="small"
					options={templates}
					value={selectedTemplate ?? null}
					inputValue={searchText}
					onInputChange={(_, value, reason) => {
						if (reason === 'reset') {
							return;
						}

						setSearchText(value);
						if (reason === 'clear') {
							onTemplateIdChange(undefined);
							return;
						}

						if (selectedTemplate && value !== selectedTemplate.name) {
							onTemplateIdChange(undefined);
						}
					}}
					onChange={(_, template, reason) => {
						if (reason === 'clear') {
							setSearchText('');
							onTemplateIdChange(undefined);
							return;
						}

						setSearchText(template?.name ?? '');
						onTemplateIdChange(template ? getTemplateStableId(template) : undefined);
					}}
					getOptionLabel={(template) => template.name ?? ''}
					isOptionEqualToValue={(option, value) => getTemplateStableId(option) === getTemplateStableId(value)}
					loading={isFetching}
					disabled={!isEditable}
					fullWidth
					disablePortal
					clearOnBlur={false}
					clearText="Clear template"
					handleHomeEndKeys
					renderInput={(params) => <TextField {...params} label="Template" required error={!templateId} />}
				/>
			</Stack>
		</Stack>
	);
}
